import { useState, type ReactNode } from 'react';
import { Check, AlertTriangle, Receipt, Smartphone, Loader2, X, Printer, Download } from 'lucide-react';
import { formatMoeda } from '@fiodecobre/shared';
import { getToken } from '../../lib/api';
import { ModalEnviarWhatsAppNF } from '../notas-fiscais/ModalEnviarWhatsAppNF';
import { ModalComprovanteVenda } from '../notas-fiscais/ModalComprovanteVenda';

export interface ResultadoEmissaoData {
  status?: string;
  numero?: number;
  serie?: string;
  protocolo?: string;
  chave?: string;
  cStat?: string;
  xMotivo?: string;
  qrCodeUrl?: string;
  id?: number;
  nfceId?: number;
  nfeId?: number;
  autorizada?: boolean;
  cancelada?: boolean;
  erro?: string;
  // Valor REAL da nota (vNF, ja com credito abatido e acrescimo somado) — preferido
  // sobre o prop valorTotal (que traz o total da venda). creditoAbatido > 0 mostra a
  // linha "credito de R$ X abatido".
  valorTotal?: number;
  creditoAbatido?: number;
}

interface Props {
  tipo: 'nfce' | 'nfe';
  resultado: ResultadoEmissaoData;
  onConcluir: () => void;
  // Whatsapp do cliente (com '+'/espacos) — pre-seleciona o contato no seletor de destinatarios.
  clienteWhatsapp?: string | null;
  // Nome pra contexto no header (Consumidor recebe aviso generico no modal de busca)
  clienteNome?: string | null;
  // Cliente cadastrado -> seletor abre no modo multi com os contatos da ficha ja marcados.
  // Consumidor/sem cadastro -> null, cai na lista de contatos pra escolher.
  clienteId?: number | null;
  // Valor total da venda — exibido em destaque no card de info da NF
  valorTotal?: string | number | null;
  // Orcamento da venda — habilita imprimir o Comprovante de Venda (sem valor fiscal)
  // quando a emissao falhou (SEFAZ instavel), pro cliente ter prova do pagamento (DA-290).
  orcamentoId?: number;
  // Bloco de contexto do documento que ORIGINOU a nota, mostrado no topo dos dois ramos
  // (autorizada e erro). Nasceu pro Fechamento da Venda CM, que precisa dizer onde os
  // titulos foram parar — a nota sozinha nao conta isso.
  resumoExtra?: ReactNode;
  // Substitui "A venda foi finalizada..." no ramo de erro. Quem chama de um fluxo que
  // nao e venda de caixa passa a frase certa.
  avisoErro?: string;
}

export function ModalResultadoEmissao({ tipo, resultado, onConcluir, clienteWhatsapp, clienteNome, clienteId, valorTotal, orcamentoId, resumoExtra, avisoErro }: Props) {
  const tipoLabel = tipo === 'nfce' ? 'NFC-e' : 'NF-e';
  const docLabel = tipo === 'nfce' ? 'Cupom' : 'DANFE';
  const id = resultado.id ?? resultado.nfceId ?? resultado.nfeId;
  const autorizada = resultado.autorizada || resultado.status === 'autorizada' || resultado.cStat === '100' || resultado.cStat === '150';
  const [imprimindo, setImprimindo] = useState(false);
  const [baixando, setBaixando] = useState(false);
  const [comprovanteAberto, setComprovanteAberto] = useState(false);

  const [enviado, setEnviado] = useState(false);
  const [modalBuscaAberto, setModalBuscaAberto] = useState(false);

  // formato so afeta NFC-e (cupom): 'cartao' = bobina 5,5cm, 'tradicional' = 80mm.
  async function imprimir(formato: 'cartao' | 'tradicional' = 'cartao') {
    if (!id) return;
    // Abre janela vazia ANTES do await — sem isso, o browser bloqueia window.open()
    // disparado depois de uma promise (perde o "user gesture").
    const win = window.open('', '_blank');
    setImprimindo(true);
    try {
      const token = getToken();
      const endpoint = tipo === 'nfce' ? 'danfce' : 'danfe';
      const qs = tipo === 'nfce' ? `?formato=${formato}` : '';
      const r = await fetch(`/api/${tipo}/${id}/${endpoint}${qs}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) throw new Error(String(r.status));
      const blob = await r.blob();
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      if (win) win.location.href = url;
      else window.open(url, '_blank');
    } catch (e) {
      if (win) win.close();
      alert('Erro ao gerar ' + docLabel + ': ' + (e as Error).message);
    } finally {
      setImprimindo(false);
    }
  }

  // Salvar em arquivo. Mesmo PDF do imprimir, so que baixado — o <a> vai ao DOM antes do
  // click (Firefox exige) e a URL so e revogada depois (revogar na mesma pilha cancela o
  // download no Chrome).
  async function baixar() {
    if (!id) return;
    setBaixando(true);
    try {
      const token = getToken();
      const endpoint = tipo === 'nfce' ? 'danfce' : 'danfe';
      const qs = tipo === 'nfce' ? '?formato=tradicional' : '';
      const r = await fetch(`/api/${tipo}/${id}/${endpoint}${qs}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) throw new Error(String(r.status));
      const url = URL.createObjectURL(new Blob([await r.blob()], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${tipoLabel}-${resultado.numero ?? id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      alert('Erro ao baixar ' + docLabel + ': ' + (e as Error).message);
    } finally {
      setBaixando(false);
    }
  }

  // O botao NUNCA envia sozinho: abre o seletor de destinatarios. Cliente cadastrado ja
  // entra com os contatos da ficha marcados; balcao/Consumidor cai na lista pra escolher.
  const clienteCadastrado = !!clienteId && clienteId !== 1 && clienteNome !== 'Consumidor';

  const avisoModalBusca = clienteCadastrado
    ? undefined
    : clienteNome && clienteNome !== 'Consumidor'
      ? `${clienteNome} nao possui WhatsApp cadastrado. Selecione um contato abaixo.`
      : 'Selecione o contato para envio.';

  return (
    <>
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
        <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md mx-4">
          {resumoExtra}
          {autorizada ? (
            <>
              {/* Header com icone, titulo, cliente e protocolo */}
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <Check size={22} className="text-green-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-green-700 text-lg">{tipoLabel} Autorizada</div>
                  {clienteNome && <div className="text-sm font-medium text-gray-700 truncate">{clienteNome}</div>}
                  <div className="text-sm text-gray-500 break-all">Protocolo: {resultado.protocolo}</div>
                </div>
              </div>

              {/* Card cinza com Total / Numero / Serie / Chave. Mostra o valor REAL da
                  nota (resultado.valorTotal) — cai pro prop so se o backend nao mandar. */}
              <div className="text-sm text-gray-600 space-y-1 bg-gray-50 rounded-lg p-3">
                {(resultado.valorTotal ?? valorTotal) != null && (
                  <div className="pb-1 mb-1 border-b border-gray-200">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400">Total da nota:</span>
                      <span style={{ fontSize: 14, fontWeight: 700 }}>{formatMoeda(resultado.valorTotal ?? valorTotal!)}</span>
                    </div>
                    {(resultado.creditoAbatido ?? 0) > 0 && (
                      <div className="text-xs text-gray-500 mt-0.5">
                        crédito de {formatMoeda(resultado.creditoAbatido!)} abatido
                      </div>
                    )}
                  </div>
                )}
                <div><span className="text-gray-400">Numero:</span> <span className="font-mono font-medium">{resultado.numero}</span></div>
                <div><span className="text-gray-400">Serie:</span> <span className="font-mono">{resultado.serie}</span></div>
                {resultado.chave && (
                  <div className="break-all"><span className="text-gray-400">Chave:</span> <span className="font-mono text-xs">{resultado.chave}</span></div>
                )}
              </div>

              {/* Botao Imprimir — azul, full width. NFC-e sai no formato Maquina de Cartao
                  por padrao, com link pra trocar pro tradicional 80mm. */}
              {id && (
                <>
                  <button
                    type="button"
                    onClick={() => void imprimir(tipo === 'nfce' ? 'cartao' : 'tradicional')}
                    disabled={imprimindo}
                    className="mt-3 w-full px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {imprimindo ? <Loader2 size={16} className="animate-spin" /> : <Receipt size={16} />}
                    Imprimir {tipo === 'nfce' ? 'Cupom (Maquina de Cartao)' : docLabel}
                  </button>
                  {tipo === 'nfce' && (
                    <button
                      type="button"
                      onClick={() => void imprimir('tradicional')}
                      disabled={imprimindo}
                      className="mt-1.5 w-full text-xs text-gray-500 hover:text-gray-700 hover:underline disabled:opacity-50"
                    >
                      ou imprimir no formato tradicional (8cm)
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => void baixar()}
                    disabled={baixando}
                    className="mt-2 w-full px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {baixando ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                    Salvar {docLabel} em PDF
                  </button>
                </>
              )}

              {/* Botao Enviar WhatsApp — verde, full width. Abre o seletor de destinatarios. */}
              {id && (
                <button
                  type="button"
                  onClick={() => setModalBuscaAberto(true)}
                  className="mt-3 w-full px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
                >
                  <Smartphone size={16} />
                  {enviado ? 'Enviar novamente' : 'Enviar WhatsApp'}
                </button>
              )}

              {/* Banner de confirmacao apos enviar */}
              {enviado && (
                <div className="mt-3 w-full px-4 py-2.5 bg-green-50 border border-green-200 rounded-lg text-center text-sm text-green-700">
                  Nota enviada pelo WhatsApp
                </div>
              )}
            </>
          ) : (
            <>
              {/* Erro / rejeicao */}
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle size={22} className="text-red-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-red-700 text-lg">
                    {resultado.status === 'rejeitada' ? `${tipoLabel} Rejeitada` : `Erro na ${tipoLabel}`}
                  </div>
                  {clienteNome && <div className="text-sm font-medium text-gray-700 truncate">{clienteNome}</div>}
                  {resultado.cStat && (
                    <div className="text-sm text-gray-500">Codigo: {resultado.cStat}</div>
                  )}
                </div>
              </div>
              <div className="text-sm text-red-700 bg-red-50 rounded-lg p-3 break-words">
                {resultado.xMotivo || resultado.erro || 'sem detalhes'}
              </div>
              <div className="text-xs text-gray-400 mt-2">
                {avisoErro ?? `A venda foi finalizada. A ${tipoLabel} pode ser emitida novamente pela aba Notas Fiscais.`}
              </div>
              {/* NAO ha "Tentar de novo" aqui (removido 2026-08-10). O botao reemitia a nota
                  no ponto exato em que o sistema AINDA NAO SABE se a primeira foi autorizada:
                  timeout/erro de comunicacao deixa a nota em `erro_comunicacao`, e a SEFAZ pode
                  ter autorizado com o retorno perdido no caminho — clicar ali era apostar em
                  nota em dobro. Quem esta no balcao nao tem como distinguir esse caso de
                  "a emissao nem comecou", e o botao era identico nos dois.
                  Os dois caminhos seguros continuam: emissao que nunca criou linha e' reemitida
                  sozinha pelo `nf-auto-emissao.scheduler` em ate 2min; nota ja registrada se
                  resolve em /notas-fiscais, onde o status real e o motivo da SEFAZ aparecem. */}
              {/* Comprovante de venda (sem valor fiscal) — pro cliente levar prova do
                  pagamento enquanto a NF nao sai (SEFAZ instavel). DA-290. */}
              {orcamentoId && (
                <button
                  type="button"
                  onClick={() => setComprovanteAberto(true)}
                  className="mt-2 w-full px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center justify-center gap-2"
                >
                  <Printer size={16} />
                  Comprovante não fiscal
                </button>
              )}
            </>
          )}

          {/* Botao Fechar */}
          <button
            type="button"
            onClick={onConcluir}
            className="mt-4 w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center justify-center gap-2 text-sm"
          >
            <X size={14} />
            Fechar
          </button>
        </div>
      </div>

      {/* Seletor de destinatarios — cliente cadastrado abre no modo multi (contatos da ficha
          ja marcados); Consumidor/balcao cai na lista de contatos, com busca e numero avulso. */}
      {modalBuscaAberto && id && (
        <ModalEnviarWhatsAppNF
          tipo={tipo}
          id={id}
          numero={resultado.numero ?? 0}
          aviso={avisoModalBusca}
          preSelecionarTelefone={clienteWhatsapp ?? null}
          clienteId={clienteCadastrado ? clienteId : null}
          clienteNome={clienteNome ?? undefined}
          onClose={(ok) => {
            setModalBuscaAberto(false);
            if (ok) setEnviado(true);
          }}
        />
      )}

      {/* Comprovante de venda (sem valor fiscal) — DA-290 */}
      {comprovanteAberto && orcamentoId && (
        <ModalComprovanteVenda
          info={{
            orcamentoId,
            clienteNome,
            clienteWhatsapp,
            valorTotal: resultado.valorTotal ?? valorTotal,
          }}
          onFechar={() => setComprovanteAberto(false)}
        />
      )}
    </>
  );
}
